import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, MenuItem, Typography } from "@mui/material";
import * as React from "react";

import styles from "./LayoutMenuSection.module.css";

const iconSx = { color: "grey.700" };

interface LayoutMenuSectionProps extends React.PropsWithChildren {
  /** Section title (e.g. "Personal Layouts"). */
  title: string;
  /** Whether the section has any layouts to display. */
  hasNoLayouts: boolean;
  /** Message shown when the section is empty. */
  hasNoLayoutsMessage?: string;
  /** Whether the section is expanded (renders its children). */
  expanded: boolean;
  /** Toggles the expanded state when the header is clicked. */
  onToggle: () => void;
}

/** A collapsible titled section of the layout picker. */
export function LayoutMenuSection({
  children,
  hasNoLayouts,
  title,
  hasNoLayoutsMessage = "No layouts yet",
  expanded,
  onToggle,
}: LayoutMenuSectionProps) {
  const ChevronIcon = expanded ? ExpandMoreIcon : ChevronRightIcon;
  return (
    <>
      <MenuItem
        disableRipple
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        // The chevron is right-aligned so section titles share a left edge with the rows below.
        sx={{ mt: 1, gap: 0.5, justifyContent: "space-between" }}
      >
        <Typography variant="subtitle2">{title}</Typography>
        {/*
         * Wrap the chevron in the same trailing footprint as the rows' three-dot icon buttons
         * (a small IconButton is an 18px glyph + 5px padding = 28px). px: "4px" pads the 20px
         * chevron to that same 28px width, so section chevrons line up with the three-dot column.
         */}
        <Box
          aria-hidden
          sx={{ display: "flex", alignItems: "center", py: "5px", px: "4px" }}
        >
          <ChevronIcon
            fontSize="small"
            sx={iconSx}
            className={styles.sectionChevron}
          />
        </Box>
      </MenuItem>
      {expanded && (
        <>
          {children}
          {hasNoLayouts && (
            <MenuItem disabled sx={{ typography: "body2" }}>
              {hasNoLayoutsMessage}
            </MenuItem>
          )}
        </>
      )}
    </>
  );
}
